import { useState, useEffect, useCallback } from 'react';
import { remindersAPI } from '../lib/api';
import { useAuth } from '../lib/auth';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Bell, MessageSquare, Mail, Phone, Send } from 'lucide-react';

const CHANNEL_ICON = { sms: Phone, whatsapp: MessageSquare, email: Mail };

const RemindersPage = () => {
  const { hospital } = useAuth();
  const hospitalId = hospital?.id;
  const [reminders, setReminders] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [genDate, setGenDate] = useState(new Date(Date.now() + 86400000).toISOString().split('T')[0]);
  const [generating, setGenerating] = useState(false);

  const load = useCallback(async () => {
    try {
      const params = hospitalId ? { hospital_id: hospitalId } : {};
      const [r1, r2] = await Promise.all([remindersAPI.list(params), remindersAPI.templates(params)]);
      setReminders(r1.data || []);
      setTemplates(r2.data || []);
    } catch { toast.error('Failed to load'); }
  }, [hospitalId]);

  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    try {
      const res = await remindersAPI.generate({ date: genDate, hospital_id: hospitalId });
      toast.success(res.data.message || 'Generated');
      load();
    } catch { toast.error('Failed'); }
    setGenerating(false);
  };

  const counts = {
    scheduled: reminders.filter(r => r.status === 'scheduled').length,
    sent: reminders.filter(r => r.status === 'sent').length,
    delivered: reminders.filter(r => r.status === 'delivered').length,
    failed: reminders.filter(r => r.status === 'failed').length,
  };

  return (
    <div className="space-y-5" data-testid="reminders-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Appointment Reminders</h1>
          <p className="text-sm text-slate-500 mt-0.5">SMS / Email / WhatsApp framework — plug in provider when ready</p>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Scheduled" value={counts.scheduled} color="text-blue-600" />
        <StatCard label="Sent" value={counts.sent} color="text-amber-600" />
        <StatCard label="Delivered" value={counts.delivered} color="text-emerald-600" />
        <StatCard label="Failed" value={counts.failed} color="text-red-600" />
      </div>

      {/* Generate */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Send size={16} /> Auto-Generate Reminders</h3>
        <div className="flex gap-3 items-end">
          <div className="flex-1"><Label>For appointments on</Label><Input type="date" value={genDate} onChange={e => setGenDate(e.target.value)} data-testid="gen-date" /></div>
          <Button onClick={generate} disabled={generating} className="bg-teal-600 hover:bg-teal-700" data-testid="generate-reminders-btn">
            {generating ? 'Generating...' : 'Generate Reminders'}
          </Button>
        </div>
      </div>

      {/* Templates */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><MessageSquare size={16} /> Message Templates</h3>
        <div className="space-y-2">
          {templates.map(t => {
            const Icon = CHANNEL_ICON[t.channel] || Bell;
            return (
              <div key={t.id} className="flex items-start gap-3 p-3 bg-slate-50/50 rounded-lg border border-slate-100">
                <Icon size={16} className="text-slate-500 mt-0.5" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-800">{t.name} <span className="text-xs text-slate-400 ml-2">{t.trigger_hours}h trigger · {t.channel}</span></p>
                  <p className="text-xs text-slate-500 mt-0.5 italic">{t.message}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Recent Reminders */}
      <div className="bg-white rounded-xl border border-slate-200 p-5">
        <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><Bell size={16} /> Recent Reminders ({reminders.length})</h3>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b text-xs uppercase text-slate-500">
                <th className="text-left py-2 px-2">Patient</th>
                <th className="text-left py-2 px-2">Doctor</th>
                <th className="text-left py-2 px-2">Appointment</th>
                <th className="text-left py-2 px-2">Channel</th>
                <th className="text-center py-2 px-2">Status</th>
              </tr>
            </thead>
            <tbody>
              {reminders.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400">No reminders yet. Generate reminders for upcoming appointments.</td></tr>
              ) : reminders.slice(0, 50).map(r => (
                <tr key={r.id} className="border-b border-slate-50">
                  <td className="py-2 px-2 font-medium text-slate-800">{r.patient_name || '—'}</td>
                  <td className="py-2 px-2 text-slate-600">{r.doctor_name || '—'}</td>
                  <td className="py-2 px-2 text-slate-600">{r.appointment_date} {r.appointment_time}</td>
                  <td className="py-2 px-2 capitalize text-slate-600">{r.channel}</td>
                  <td className="py-2 px-2 text-center">
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      r.status === 'delivered' ? 'bg-emerald-50 text-emerald-700' :
                      r.status === 'sent' ? 'bg-amber-50 text-amber-700' :
                      r.status === 'failed' ? 'bg-red-50 text-red-700' :
                      'bg-blue-50 text-blue-700'
                    }`}>{r.status}</span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

const StatCard = ({ label, value, color }) => (
  <div className="bg-white rounded-xl border border-slate-200 p-4">
    <p className="text-xs text-slate-500 uppercase font-semibold">{label}</p>
    <p className={`text-2xl font-bold mt-1 ${color}`}>{value}</p>
  </div>
);

export default RemindersPage;
